import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import {
  BrandPlan,
  BrandPlanSchedule,
  BrandPlanTactic,
  PreviewTopicsResponse,
  BrandPlanTopics,
  BrandPlanTacticContents,
  BrandPlanContents,
} from 'types'
import { FormValues } from './Types'
import { RefObject } from 'react'
import { FormikContextType } from 'formik'

export type SubmitValues = FormValues & {
  update_all?: boolean
}

const groupSocialTopics = (topics: Partial<BrandPlanTopics>): string[] => [
  ...(topics.facebook_topics ?? []),
  ...(topics.instagram_topics ?? []),
  ...(topics.linkedin_topics ?? []),
  ...(topics.twitter_topics ?? []),
]

const groupVideoTopics = (topics: Partial<BrandPlanTopics>): string[] => [
  ...(topics.tiktok_topics ?? []),
  ...(topics.youtube_topics ?? []),
  ...(topics.video_crosspost_topics ?? []),
]

const splitInstagramContentsByType = (contents: any[] | null | undefined) => ({
  post: contents?.filter((item) => item.type === 'post') ?? [],
  story: contents?.filter((item) => item.type === 'story') ?? [],
  reel: contents?.filter((item) => item.type === 'reel') ?? [],
})

const mergeInstagramContents = (post: any[], story: any[], reel: any[]) => [
  ...(post || []).map((item) => ({ ...item, type: 'post' })),
  ...(story || []).map((item) => ({ ...item, type: 'story' })),
  ...(reel || []).map((item) => ({ ...item, type: 'reel' })),
]

export const crossPostProvidersFromTactics = (tactics: string[]) => {
  return [
    tactics.includes(BrandPlanTactic.Instagram) && ExternalIntegrationEnum.Instagram,
    tactics.includes(BrandPlanTactic.Facebook) && ExternalIntegrationEnum.Facebook,
    tactics.includes(BrandPlanTactic.Twitter) && ExternalIntegrationEnum.X,
    tactics.includes(BrandPlanTactic.LinkedIn) && ExternalIntegrationEnum.LinkedIn,
    tactics.includes(BrandPlanTactic.GoogleBusinessProfile) && ExternalIntegrationEnum.GoogleBusinessProfile,
  ].filter(Boolean) as ExternalIntegrationEnum[]
}

export const hasContentCountsChanged = (values: SubmitValues, initialValues: FormValues): boolean => {
  const baseFields: (keyof FormValues)[] = [
    'facebook_contents',
    'instagram_post_contents',
    'instagram_story_contents',
    'instagram_reel_contents',
    'twitter_contents',
    'linkedin_contents',
    'tiktok_contents',
    'youtube_contents',
    'seo_contents',
    'email_contents',
  ]

  const hasChanged = (field: keyof FormValues) => JSON.stringify(values[field]) !== JSON.stringify(initialValues[field])

  const baseChanged = baseFields.some(hasChanged)

  return (
    baseChanged ||
    hasChanged('crosspost_contents') ||
    hasChanged('video_crosspost_contents') ||
    hasChanged('google_my_business_contents')
  )
}

interface ModalConfig {
  title: string
  prompt: string
}

export const getConfirmationModalConfig = (hasPostCountsChanged: boolean, hasTacticsChanged: boolean): ModalConfig => {
  if (hasPostCountsChanged && hasTacticsChanged) {
    return {
      title: 'Confirm Changes',
      prompt:
        'Would you like to update the post frequency and cross post channels only for this week, or make them your default?',
    }
  }

  if (hasPostCountsChanged) {
    return {
      title: 'Confirm Post Frequency',
      prompt: 'Would you like to update the number of posts only for this week, or make it your default?',
    }
  }

  return {
    title: 'Confirm Cross Post Channels',
    prompt: 'Would you like to update the cross post channels only for this week, or make it your default?',
  }
}

export const transformFormValuesToPayload = (values: SubmitValues): any => {
  const {
    social_topics = [],
    video_topics = [],
    tactics,
    instagram_post_contents,
    instagram_story_contents,
    instagram_reel_contents,
    crosspost_contents,
    crosspost_topics,
    video_crosspost_contents,
    video_crosspost_topics,
    approval_date,
    week_days,
    daily_frequency,
    crosspost_frequency,
    update_all,
    ...rest
  } = values

  const basePayload = {
    ...rest,
    instagram_contents: mergeInstagramContents(
      instagram_post_contents || [],
      instagram_story_contents || [],
      instagram_reel_contents || [],
    ),
    instagram_topics: social_topics,
    facebook_topics: [],
    linkedin_topics: [],
    twitter_topics: [],
  }

  return {
    ...basePayload,
    tactics,
    crosspost_contents,
    crosspost_topics: [],
    video_crosspost_contents,
    video_crosspost_topics: [],
    approval_date,
    week_days,
    daily_frequency,
    crosspost_frequency,
    update_all,
    tiktok_topics: video_topics,
    youtube_topics: [],
  }
}

export const getInitialPlan = (planOrSchedule: BrandPlan | BrandPlanSchedule | undefined): FormValues => {
  const {
    facebook_contents,
    instagram_contents,
    twitter_contents,
    linkedin_contents,
    seo_contents,
    seo_topics,
    email_contents,
    email_topics,
    tiktok_contents,
    youtube_contents,
    crosspost_contents,
    crosspost_topics,
    video_crosspost_contents,
    video_crosspost_topics,
    google_my_business_contents,
    google_my_business_topics,
    tactics,
    approval_date,
    start_date,
    week_days,
    daily_frequency,
    crosspost_frequency,
  } = planOrSchedule || {}

  const { post, story, reel } = splitInstagramContentsByType(instagram_contents)

  return {
    social_topics: groupSocialTopics(planOrSchedule || {}),
    video_topics: groupVideoTopics(planOrSchedule || {}),
    facebook_contents: facebook_contents ?? [],
    instagram_post_contents: post,
    instagram_story_contents: story,
    instagram_reel_contents: reel,
    twitter_contents: twitter_contents ?? [],
    linkedin_contents: linkedin_contents ?? [],
    seo_contents: seo_contents ?? [],
    seo_topics: seo_topics ?? [],
    email_contents: email_contents ?? [],
    email_topics: email_topics ?? [],
    tiktok_contents: tiktok_contents ?? [],
    youtube_contents: youtube_contents ?? [],
    crosspost_contents: crosspost_contents ?? [],
    crosspost_topics: crosspost_topics ?? [],
    video_crosspost_contents: video_crosspost_contents ?? [],
    video_crosspost_topics: video_crosspost_topics ?? [],
    google_my_business_contents: google_my_business_contents ?? [],
    google_my_business_topics: google_my_business_topics ?? [],
    tactics: tactics || [],
    start_date: start_date ?? '',
    approval_date: approval_date ?? '',
    week_days: week_days ?? [],
    daily_frequency: daily_frequency ?? 'once_a_day',
    crosspost_frequency: crosspost_frequency ?? 'always',
  }
}

export const applyPreviewTopicsToFormik = (
  preview: PreviewTopicsResponse,
  formikRef: RefObject<FormikContextType<any>>,
) => {
  if (!preview.topics || !formikRef.current) return

  const { email_topics, seo_topics } = preview.topics

  const socialTopics = groupSocialTopics(preview.topics)
  if (socialTopics.length > 0) {
    formikRef.current.setFieldValue('social_topics', socialTopics)
  }

  const videoTopics = groupVideoTopics(preview.topics)
  if (videoTopics.length > 0) {
    formikRef.current.setFieldValue('video_topics', videoTopics)
  }

  if (email_topics) {
    formikRef.current.setFieldValue('email_topics', email_topics)
  }

  if (seo_topics) {
    formikRef.current.setFieldValue('seo_topics', seo_topics)
  }
}

/**
 * Maps ExternalIntegrationEnum provider to the corresponding brand plan contents field name.
 * Used when disconnecting integrations to clear the associated brand plan contents.
 */
export const PROVIDER_TO_CONTENTS_FIELD: Partial<Record<ExternalIntegrationEnum, keyof BrandPlanContents>> = {
  [ExternalIntegrationEnum.Instagram]: 'instagram_contents',
  [ExternalIntegrationEnum.Facebook]: 'facebook_contents',
  [ExternalIntegrationEnum.LinkedIn]: 'linkedin_contents',
  [ExternalIntegrationEnum.X]: 'twitter_contents',
  [ExternalIntegrationEnum.TikTok]: 'tiktok_contents',
  [ExternalIntegrationEnum.YouTube]: 'youtube_contents',
  [ExternalIntegrationEnum.Mailchimp]: 'email_contents',
  [ExternalIntegrationEnum.WordPress]: 'seo_contents',
  [ExternalIntegrationEnum.GoHighLevel]: 'seo_contents',
  [ExternalIntegrationEnum.Wix]: 'seo_contents',
  [ExternalIntegrationEnum.GoogleBusinessProfile]: 'google_my_business_contents',
} as const

/**
 * Returns the default contents structure for a given provider with specified counts.
 * This ensures the correct content types are preserved when setting contents.
 * @param provider - The integration provider
 * @param count - The count to set for all content types (defaults to 0)
 */
export const getContentsStructureForProvider = (
  provider: ExternalIntegrationEnum,
  count: number = 0,
): BrandPlanTacticContents | null => {
  switch (provider) {
    case ExternalIntegrationEnum.Instagram:
      return [
        { type: 'post', count },
        { type: 'story', count },
        { type: 'reel', count },
      ]
    case ExternalIntegrationEnum.Facebook:
      return [{ type: 'post', count }]
    case ExternalIntegrationEnum.LinkedIn:
      return [{ type: 'post', count }]
    case ExternalIntegrationEnum.X:
      return [{ type: 'post', count }]
    case ExternalIntegrationEnum.TikTok:
      return [{ type: 'post', count }]
    case ExternalIntegrationEnum.YouTube:
      return [{ type: 'short', count }]
    case ExternalIntegrationEnum.Mailchimp:
      return [{ type: 'newsletter', count }]
    case ExternalIntegrationEnum.WordPress:
    case ExternalIntegrationEnum.GoHighLevel:
    case ExternalIntegrationEnum.Wix:
      return [{ type: 'blog_post', count }]
    case ExternalIntegrationEnum.GoogleBusinessProfile:
      return [{ type: 'post', count }]
    default:
      return null
  }
}
